import { LogManager } from '@src/utility/Logger';
import { observable, runInAction } from 'mobx';

import { registerTtfeConsoleCommands } from './ttfeConsole';

/**
 * Dev override for testing the Faster TTFE pending window: hold every story
 * this session saw pending on its audio-first / local-preview state even after
 * the backend's renditions land, until the hand-off is triggered by hand.
 *
 *   localStorage['ttfe.manualUpgrade'] = '1'   // arm (remove the key to disarm)
 *   __ttfe.upgrade()                          // release: the next readiness
 *                                             // poll runs the normal hand-off
 *   __ttfe.hold()                             // re-arm for the next upload
 *   __ttfe.status()
 *
 * The readiness polls keep running (they also load the transcript and set up
 * the local canvas); only their view of "renditions landed" is held. Failed
 * pipelines are never held. For local and preview testing only.
 */
export const MANUAL_RENDITION_HANDOFF_KEY = 'ttfe.manualUpgrade';

const logger = LogManager.getLogger('ttfe:manual-handoff');

/** Stories observed pending this session — only those are ever held. */
const seenPending = new Set<string>();
const released = observable.box(false);

export function isManualRenditionHandOffEnabled(): boolean {
  try {
    return (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(MANUAL_RENDITION_HANDOFF_KEY) === '1'
    );
  } catch {
    return false;
  }
}

/**
 * Whether a story the pipeline reports ready (`pending === false`) should
 * still be treated as pending. Records pending stories as a side effect.
 */
export function isRenditionHandOffHeld(
  storyId: string | undefined,
  pending: boolean,
  failed: boolean,
): boolean {
  if (!storyId || !isManualRenditionHandOffEnabled()) return false;
  if (pending) {
    seenPending.add(storyId);
    return false;
  }
  return !failed && seenPending.has(storyId) && !released.get();
}

export function releaseRenditionHandOff(): void {
  runInAction(() => released.set(true));
}

export function rearmRenditionHandOff(): void {
  seenPending.clear();
  runInAction(() => released.set(false));
}

registerTtfeConsoleCommands({
  upgrade: () => {
    if (!isManualRenditionHandOffEnabled()) {
      logger.warn(
        `Manual hand-off is off: set localStorage['${MANUAL_RENDITION_HANDOFF_KEY}'] = '1' and reload`,
      );
      return;
    }
    releaseRenditionHandOff();
    logger.info(
      'Released: the next readiness poll swaps to the renditions once the backend reports them',
      { heldStories: [...seenPending] },
    );
  },
  hold: () => {
    rearmRenditionHandOff();
    logger.info('Re-armed: stories seen pending from now on are held');
  },
  status: () => ({
    enabled: isManualRenditionHandOffEnabled(),
    released: released.get(),
    heldStories: [...seenPending],
  }),
});
